import React, { useCallback, useMemo } from 'react';
import BaseTrimHandles from './BaseTrimHandles';
import { setCurrentTimeWithoutSeeking } from '../store/remotionPlayerState';
import { MIN_VIDEO_SEGMENT_DURATION } from '../timeUtils';
import { updateClipData } from '../clipUpdateDataUtils';
import { CLIP_UPDATE_TYPE } from '../clipUpdateDataUtilsWithSplits/constants';
import { roundToNDecimalPlaces } from '@/libs/utils';
import { VideoSegment } from '@/App/remotion/MainPlayer/types/Segment/VideoSegment';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { Clip } from '@/domains/asset';

interface VideoSegmentTrimHandlesProps {
  fps: number;
  clipId: string;
  clipData: Clip;
  showTrimsBars: 'start' | 'end' | null;
  segment: VideoSegment;
  videoSegmentDragStart: number;
  videoSegmentDragEnd: number;
  setVideoSegmentDragStart: (value: number) => void;
  setVideoSegmentDragEnd: (value: number) => void;
  newTimelineContainerRef: React.RefObject<HTMLDivElement>;
  speakerTimelineContainerRef: React.RefObject<HTMLDivElement>;
}

export default function VideoSegmentTrimHandles({
  fps,
  clipId,
  clipData,
  showTrimsBars,
  segment,
  videoSegmentDragStart,
  videoSegmentDragEnd,
  setVideoSegmentDragStart,
  setVideoSegmentDragEnd,
  newTimelineContainerRef,
  speakerTimelineContainerRef
}: Readonly<VideoSegmentTrimHandlesProps>): React.ReactElement | null {
  const { seekTo } = useRemotionPlayerSeek();

  const segmentStartTime = useMemo(() => {
    if (segment.segment_metadata?.start !== undefined) {
      return segment.segment_metadata.start;
    }
    return segment.currentBounds?.fromFrame ? segment.currentBounds.fromFrame / fps : 0;
  }, [segment.segment_metadata?.start, segment.currentBounds?.fromFrame, fps]);

  const segmentEndTime = useMemo(() => {
    if (segment.segment_metadata?.end !== undefined) {
      return segment.segment_metadata.end;
    }
    return segment.currentBounds ? segment.currentBounds.toFrame / fps : 0;
  }, [segment.segment_metadata?.end, segment.currentBounds, fps]);

  const maxVideoDuration = useMemo(() => {
    return segment.segment_metadata?.maxVideoDuration ?? 0;
  }, [segment.segment_metadata?.maxVideoDuration]);

  const onStartDragEnd = useCallback(
    (offset: number) => {
      const newTime = roundToNDecimalPlaces(segmentStartTime + offset, 3);
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_VIDEO_SEGMENT_TRACK,
        segmentId: segment.id,
        startTime: Math.max(0, newTime)
      });

      if (segment.currentBounds) {
        const newSeekTime = segment.currentBounds.fromFrame / fps + 0.01;
        seekTo(roundToNDecimalPlaces(newSeekTime, 3));
      }
    },
    [segmentStartTime, clipId, segment.id, segment.currentBounds, fps, seekTo]
  );

  const onEndDragEnd = useCallback(
    (offset: number) => {
      const newTime = roundToNDecimalPlaces(segmentEndTime + offset, 3);
      const boundedEndTime = maxVideoDuration ? Math.min(newTime, maxVideoDuration) : newTime;
      updateClipData({
        clipId,
        actionType: CLIP_UPDATE_TYPE.UPDATE_VIDEO_SEGMENT_TRACK,
        segmentId: segment.id,
        endTime: boundedEndTime
      });

      if (segment.currentBounds) {
        const newSeekTime = segment.currentBounds.toFrame / fps + 0.01;
        setCurrentTimeWithoutSeeking(newSeekTime);
      }
    },
    [segmentEndTime, maxVideoDuration, clipId, segment.id, segment.currentBounds, fps]
  );

  const getLeftTrimBounds = useCallback(() => {
    if (!segment.currentBounds) {
      return { maxOffset: 0, minOffset: 0 };
    }
    const currentDuration = (segment.currentBounds.toFrame - segment.currentBounds.fromFrame) / fps;
    return {
      minOffset: -(segment.currentBounds.fromFrame / fps),
      maxOffset: currentDuration - MIN_VIDEO_SEGMENT_DURATION
    };
  }, [segment.currentBounds, fps]);

  const getRightTrimBounds = useCallback(() => {
    if (!segment.currentBounds) {
      return { maxOffset: 0, minOffset: 0 };
    }
    const currentDuration = (segment.currentBounds.toFrame - segment.currentBounds.fromFrame) / fps;
    let maxOffset = 0;

    if (maxVideoDuration) {
      maxOffset = maxVideoDuration - segmentEndTime;
    } else if (segment.maxBounds) {
      maxOffset = (segment.maxBounds.toFrame - segment.currentBounds.toFrame) / fps;
    }

    return {
      minOffset: -(currentDuration - MIN_VIDEO_SEGMENT_DURATION),
      maxOffset
    };
  }, [segment.currentBounds, segment.maxBounds, fps, maxVideoDuration, segmentEndTime]);

  if (!segment.currentBounds) {
    return null;
  }

  return (
    <BaseTrimHandles
      fps={fps}
      clipData={clipData}
      showTrimsBars={showTrimsBars}
      segmentId={segment.id}
      dragStart={videoSegmentDragStart}
      dragEnd={videoSegmentDragEnd}
      setDragStart={setVideoSegmentDragStart}
      setDragEnd={setVideoSegmentDragEnd}
      newTimelineContainerRef={newTimelineContainerRef}
      speakerTimelineContainerRef={speakerTimelineContainerRef}
      currentBoundsFromFrame={segment.currentBounds.fromFrame}
      currentBoundsToFrame={segment.currentBounds.toFrame}
      resetMode="skip-during-drag"
      segmentStartBound={segment.currentBounds.fromFrame}
      segmentEndBound={segment.currentBounds.toFrame}
      onStartDragEnd={onStartDragEnd}
      onEndDragEnd={onEndDragEnd}
      getLeftTrimBounds={getLeftTrimBounds}
      getRightTrimBounds={getRightTrimBounds}
    />
  );
}
